import React, { useState, useEffect } from "react";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	CircularProgress,
	Box,
	Button,
	Chip,
	Avatar,
	Typography,
} from "@mui/material";
import axios from "axios";
import { GlobalState } from "context/GlobalContext";
import { Check, CheckCircleOutline, DoneAllTwoTone } from "@mui/icons-material";

function ExclusionTable() {
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [totalCount, setTotalCount] = useState(0);

	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const fetchData = async (page, rowsPerPage) => {
		setLoading(true);
		try {
			const res = await axios.get("/api/exclusion/get-exclusions", {
				params: { page: page + 1, limit: rowsPerPage }, // backend expects page (1-indexed) + limit
			});

			// Expect response like { rows: [...], total: number }
			setData(res.data.rows || []);
			setTotalCount(res.data.total || 0);
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Failed to fetch data",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchData(page, rowsPerPage);
	}, [page, rowsPerPage]);

	const headers = data.length > 0 ? Object.keys(data[0]) : [];

	// Pagination handlers
	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleApprove = async (row) => {
		await axios
			.post("/api/exclusion/approve", { id: row.ORDER_ID || row.VOUCHER_ID, type: row?.TYPE })
			.then((res) => {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: res?.data?.message,
					severity: "success",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
				fetchData(page, rowsPerPage);
			})
			.catch((err) => {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: err?.message,
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			});
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" p={3}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<>
			<TableContainer component={Paper} sx={{ mt: 2 }}>
				<Table>
					<TableHead>
						<TableRow>
							{headers.map((header) => (
								<TableCell key={header} sx={{ fontWeight: "bold" }}>
									{header.toUpperCase()}
								</TableCell>
							))}
							<TableCell sx={{ fontWeight: "bold" }}>ACTIONS</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((row, index) => (
							<TableRow key={index}>
								{headers.map((header) => (
									<TableCell key={header}>
										<Typography
											noWrap
											variant="body2"
											width={header === "CREATED_BY" || header === "APPROVED_BY" ? "100px" : "auto"}>
											{row[header]?.value || row[header] || "-"}
										</Typography>
									</TableCell>
								))}
								<TableCell>
									{row.APPROVED_ON ? (
										<Chip
											label="Approved"
											variant="contained"
											avatar={
												<Avatar sx={{ bgcolor: "success.main" }}>
													<CheckCircleOutline fontSize="small" sx={{ color: "white" }} />
												</Avatar>
											}
											color="success"></Chip>
									) : (
										<Button
											size="small"
											variant="contained"
											color="primary"
											onClick={() => handleApprove(row)}
											sx={{ textTransform: "capitalize" }}>
											Approve
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<TablePagination
				component="div"
				count={totalCount}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				rowsPerPageOptions={[5, 10, 20, 50]}
			/>
		</>
	);
}

export default ExclusionTable;
